/**
 * Registre des couches et de leurs REGLAGES D'AFFICHAGE.
 *
 * POURQUOI ICI, ET PAS DANS LE DATAPACK. Le principe du pipeline est que les
 * DONNEES se decrivent elles-memes : un indicateur declare ses bornes, ses
 * modalites, sa rampe (cf. indicators.py / indicatorEngine.js). Un curseur
 * "epaisseur des cours d'eau x1,4" ne decrit aucune donnee — c'est une
 * decision de RENDU, qui n'existe que parce que le viewer dessine des rubans.
 * Elle appartient donc au front, au meme titre que les palettes.
 *
 * En revanche, tout ce qui PEUPLE ces reglages vient bien du datapack : la
 * liste des indicateurs communaux, les variables climatiques, les horizons
 * disponibles. Un controle designe sa source par un chemin (`options:
 * "climate.variables"`) resolu a l'execution contre le catalogue construit
 * depuis les fichiers charges. Ajouter une variable a la source la fait
 * apparaitre dans le selecteur sans toucher ce fichier.
 *
 * CE QUE CE REGISTRE REND POSSIBLE. Il est lu par TROIS consommateurs :
 *   1. le composer de vue du builder (ViewComposer), qui fabrique son
 *      formulaire a partir de `controls` ;
 *   2. le panneau de l'animateur dans le viewer, qui affiche les memes
 *      controles en version compacte ;
 *   3. le viewer lui-meme, qui applique les valeurs a la scene.
 *
 * Ajouter un reglage = une entree ici. Il apparait dans le builder, devient
 * persistable dans la vue de l'auteur, et se retrouve dans le panneau
 * animateur — sans ecrire un formulaire.
 */

// NOTE. Le champ `group` de chaque couche ci-dessous est un vestige : le
// rangement présenté à l'auteur est désormais THÉMATIQUE et vient du manifeste
// (build_manifest.py), parce qu'un thème traverse les fichiers — la
// vulnérabilité des nappes vit dans la couche communes mais parle de la
// ressource, pas de la pression humaine. Le champ est conservé pour ne pas
// casser d'éventuels usages, il n'ordonne plus rien.

/**
 * `draped: true` marque les nappes plaquees sur le relief. Elles occupent
 * toutes la meme surface et se masquent donc mutuellement : le composer s'en
 * sert pour prevenir l'auteur qu'il en a empile deux (voir ViewComposer).
 */
export const LAYER_SPECS = {
  rivers: {
    label: "Cours d'eau",
    group: "territoire",
    kind: "vector",
    summary: "Le réseau hydrographique drapé sur le relief, épaisseur proportionnelle à la longueur du cours d'eau.",
    controls: [
      {
        id: "width_scale", type: "range", label: "Épaisseur",
        min: 0.3, max: 3, step: 0.1, default: 1, format: "multiplier",
      },
      {
        id: "length_cap_km", type: "range", label: "Épaisseur maximale au-delà de",
        scale: "log", unit: "km",
        min: "rivers.lengthMin", max: "rivers.lengthMax", default: "rivers.lengthMax",
        format: "integer",
      },
    ],
  },

  cities: {
    label: "Villes",
    group: "territoire",
    kind: "markers",
    summary: "Étiquettes des communes les plus peuplées, dérivées du recensement.",
    controls: [
      {
        id: "min_population", type: "range", label: "Population minimale",
        min: 0, max: "cities.populationMax", step: 100, default: 0,
        format: "integer", unit: "hab.",
      },
      {
        id: "max_count", type: "range", label: "Nombre d'étiquettes",
        min: 1, max: "cities.count", step: 1, default: "cities.count",
        format: "integer",
      },
      {
        // Le vrai remède à l'effet "banlieue" : deux villes proches ne
        // méritent pas deux étiquettes, la plus peuplée absorbe l'autre.
        id: "min_spacing_km", type: "range", label: "Espacement minimal",
        min: 0, max: 30, step: 1, default: 0, format: "integer", unit: "km",
        help: "Écarte une ville si une ville plus peuplée se trouve à moins de cette distance. Évite les grappes de communes de banlieue.",
      },
      {
        id: "hidden", type: "exclusions", label: "Villes masquées",
        default: [], optionsKey: "cities.list",
        help: "Retire une ville précise, quels que soient les seuils.",
      },
    ],
  },

  climate: {
    label: "Climat",
    group: "ressource",
    kind: "surface",
    draped: true,
    // Le controle qui porte "ce que la couche montre", et donc la cible d'un
    // resultat de recherche du composer : choisir "Precipitations" doit
    // allumer la couche ET positionner ce controle.
    indicatorControl: "variable",
    summary: "Bilan hydrique interpolé depuis les points de mesure, par variable, horizon et mois.",
    controls: [
      { id: "variable", type: "select", label: "Variable", options: "climate.variables", default: null },
      { id: "horizon", type: "segmented", label: "Horizon", options: "climate.horizons", default: null },
      { id: "period", type: "select", label: "Période", options: "climate.periods", default: "annuel" },
      {
        // Voir createClimateInterpolator : sans ce terme, l'interpolation est
        // EXACTE et fabrique un disque de couleur autour de chaque station.
        id: "smoothing_km", type: "range", label: "Lissage de l'interpolation",
        min: 0, max: 20, step: 0.5, default: 6, format: "decimal", unit: "km",
        help: "À 0 la nappe passe exactement par chaque point de mesure et dessine un halo autour de lui. Augmenter lisse le champ entre les stations.",
      },
      { id: "opacity", type: "range", label: "Opacité", min: 0.1, max: 1, step: 0.05, default: 0.55, format: "percent" },
      { id: "stations", type: "toggle", label: "Points de mesure", default: true },
    ],
  },

  discharge: {
    label: "Débit des cours d'eau",
    group: "ressource",
    kind: "markers",
    indicatorControl: "metric",
    summary: "Stations hydrométriques. Jamais interpolé : un débit ne vaut qu'au point où il est mesuré.",
    controls: [
      { id: "metric", type: "select", label: "Grandeur", options: "discharge.metrics", default: null },
      { id: "horizon", type: "segmented", label: "Horizon", options: "discharge.horizons", default: null },
    ],
  },

  geology: {
    label: "Géologie",
    group: "ressource",
    kind: "surface",
    draped: true,
    indicatorControl: "indicator",
    summary: "Entités hydrogéologiques affleurantes (BD LISA), selon quatre classements.",
    controls: [
      { id: "indicator", type: "select", label: "Classement", options: "geology.indicators", default: "geology.defaultIndicator" },
      { id: "opacity", type: "range", label: "Opacité", min: 0.1, max: 1, step: 0.05, default: 0.8, format: "percent" },
    ],
  },

  communes: {
    label: "Communes",
    group: "pression",
    kind: "surface",
    draped: true,
    indicatorControl: "indicator",
    summary: "Choroplèthe communale : un raster d'identité, coloré par l'indicateur choisi.",
    controls: [
      { id: "indicator", type: "select", label: "Indicateur", options: "communes.indicators", default: "communes.defaultIndicator" },
      { id: "opacity", type: "range", label: "Opacité", min: 0.1, max: 1, step: 0.05, default: 0.55, format: "percent" },
      { id: "borders", type: "toggle", label: "Limites communales", default: true },
    ],
  },

  withdrawals: {
    label: "Prélèvements",
    group: "pression",
    kind: "markers",
    summary: "Ouvrages de prélèvement déclarés (BNPE / Hub'Eau), hauteur proportionnelle au volume.",
    controls: [
      { id: "bar_width", type: "range", label: "Largeur des cylindres", min: 0.4, max: 2.5, step: 0.1, default: 1, format: "multiplier" },
      { id: "bar_height", type: "range", label: "Hauteur des cylindres", min: 0.4, max: 3, step: 0.1, default: 1.35, format: "multiplier" },
      {
        id: "usages", type: "exclusions", label: "Usages masqués",
        default: [], optionsKey: "withdrawals.usages",
      },
    ],
  },
};

// Ordre d'affichage du selecteur de couches : celui du registre, qui suit
// l'ordre des groupes.
export const LAYER_ORDER = Object.keys(LAYER_SPECS);

export function layerLabel(layerId) {
  return LAYER_SPECS[layerId]?.label || layerId;
}

/** Les couches drapees actives, pour signaler un empilement qui se masque. */
export function drapedLayers(layerIds) {
  return (layerIds || []).filter((id) => LAYER_SPECS[id]?.draped);
}

// --- Catalogue : ce que le datapack charge apporte aux controles ----------

/**
 * Resout une valeur de controle qui peut etre soit litterale (nombre, bool,
 * tableau), soit un CHEMIN dans le catalogue (`"cities.populationMax"`). Ce
 * detour est ce qui permet a un curseur d'avoir des bornes tirees des donnees
 * reelles plutot que d'un ordre de grandeur devine a l'ecriture.
 */
export function resolveCatalogValue(value, catalog) {
  if (typeof value !== "string") return value;
  const parts = value.split(".");
  let node = catalog;
  for (const part of parts) {
    if (node == null) return undefined;
    node = node[part];
  }
  return node;
}

/**
 * Options d'un controle `select`/`segmented`, normalisees en
 * `[{ value, label, group? }]`. Renvoie un tableau vide si le datapack n'a
 * pas cette dimension — le controle est alors masque plutot que propose vide.
 */
export function resolveControlOptions(control, catalog) {
  const raw = resolveCatalogValue(control.options, catalog);
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => {
    if (typeof entry === "string") return { value: entry, label: entry };
    return { value: entry.id ?? entry.value, label: entry.label ?? entry.id, group: entry.group };
  });
}

export function resolveControlBounds(control, catalog) {
  const min = resolveCatalogValue(control.min, catalog);
  const max = resolveCatalogValue(control.max, catalog);
  return {
    min: Number.isFinite(min) ? min : 0,
    max: Number.isFinite(max) ? max : 1,
  };
}

/**
 * Valeur par defaut d'un controle. `default: null` sur un selecteur signifie
 * "ce que le datapack designe lui-meme" (son indicateur par defaut, son
 * premier horizon) : l'auteur qui n'a rien choisi ne doit pas figer un choix
 * arbitraire du front.
 */
export function defaultControlValue(control, catalog) {
  const resolved = resolveCatalogValue(control.default, catalog);
  if (resolved !== undefined && resolved !== null) return resolved;
  // Un selecteur sans defaut utilisable retombe sur sa premiere option : le
  // datapack a pu ne pas designer de defaut (`default_indicator` absent), ou
  // le designer sur un indicateur qui a ete ecarte faute de valeurs.
  if (control.type === "select" || control.type === "segmented") {
    return resolveControlOptions(control, catalog)[0]?.value ?? null;
  }
  return control.default === null ? undefined : control.default;
}

/** Reglages complets d'une couche, defauts du registre appliques. */
export function defaultSettingsFor(layerId, catalog = {}) {
  const spec = LAYER_SPECS[layerId];
  if (!spec) return {};
  const settings = {};
  spec.controls.forEach((control) => {
    const value = defaultControlValue(control, catalog);
    if (value !== undefined) settings[control.id] = value;
  });
  return settings;
}

export function defaultLayerSettings(catalog = {}) {
  return Object.fromEntries(LAYER_ORDER.map((id) => [id, defaultSettingsFor(id, catalog)]));
}

/**
 * Empile trois niveaux, du plus general au plus specifique : defauts du
 * registre < reglages de l'auteur (la vue enregistree) < reglages de
 * l'animateur (ephemeres, jamais persistes). C'est cette pile qui permet a
 * l'animateur d'improviser sans jamais abimer la vue de l'auteur : ses
 * modifications vivent dans le troisieme etage, remis a zero au changement
 * d'etape.
 */
export function mergeLayerSettings(...levels) {
  const merged = {};
  levels.forEach((level) => {
    if (!level) return;
    Object.entries(level).forEach(([layerId, settings]) => {
      if (!settings) return;
      merged[layerId] = { ...(merged[layerId] || {}), ...settings };
    });
  });
  return merged;
}

/**
 * Retire des reglages d'auteur tout ce qui est egal au defaut : une vue
 * enregistree ne doit porter que ce que l'auteur a REELLEMENT decide. Sinon
 * changer un defaut du registre ne changerait plus rien aux vues existantes,
 * qui l'auraient toutes fige a la valeur du jour de leur creation.
 */
export function pruneLayerSettings(settings, catalog = {}) {
  const pruned = {};
  Object.entries(settings || {}).forEach(([layerId, layerSettings]) => {
    const spec = LAYER_SPECS[layerId];
    if (!spec || !layerSettings) return;
    const kept = {};
    spec.controls.forEach((control) => {
      const value = layerSettings[control.id];
      if (value === undefined) return;
      const fallback = defaultControlValue(control, catalog);
      if (JSON.stringify(value) === JSON.stringify(fallback)) return;
      kept[control.id] = value;
    });
    if (Object.keys(kept).length) pruned[layerId] = kept;
  });
  return pruned;
}

// --- Formatage des valeurs de controle (builder + panneau animateur) ------

export function formatControlValue(control, value) {
  if (value === undefined || value === null) return "—";
  switch (control.format) {
    case "percent":
      return `${Math.round(value * 100)} %`;
    case "multiplier":
      return `×${Number(value).toFixed(1)}`;
    case "integer":
      return `${Math.round(value).toLocaleString("fr-FR")}${control.unit ? ` ${control.unit}` : ""}`;
    case "decimal":
      return `${Number(value).toLocaleString("fr-FR", { maximumFractionDigits: 1 })}${control.unit ? ` ${control.unit}` : ""}`;
    default:
      return `${value}${control.unit ? ` ${control.unit}` : ""}`;
  }
}
